import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BOOKING_URL, IMAGES } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";

const ease = [0.22, 1, 0.36, 1];

const HeroLine = ({ children, delay }) => {
  const reduced = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: reduced ? 0 : "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.4, delay, ease }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (reduced && videoRef.current) videoRef.current.pause();
  }, [reduced]);

  return (
    <section
      id="top"
      data-chapter="01"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen overflow-hidden bg-forest text-ivory"
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        poster={IMAGES.hero}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        data-testid="hero-video"
      >
        <source src="/videos/hero-client.webm" type="video/webm" />
        <source src="/videos/hero-client.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-forest/60" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-gradient-to-r from-forest/75 via-forest/35 to-transparent"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid min-h-screen max-w-[1500px] items-center gap-10 px-6 pb-20 pt-28 lg:grid-cols-12 lg:gap-10 lg:px-14 lg:pb-20 lg:pt-28">
        <div className="flex flex-col justify-center lg:col-span-7">
          <h1 className="font-serif text-4xl font-light leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <HeroLine delay={0.45}>Therapeutic Bodywork.</HeroLine>
            <HeroLine delay={0.6}>
              <span className="italic text-sand">Focused Skin Care.</span>
            </HeroLine>
            <HeroLine delay={0.75}>
              Restorative Treatments
              <span className="mb-2 ml-3 inline-block h-2 w-2 rounded-full bg-gold align-baseline" />
            </HeroLine>
          </h1>

          <motion.div
            className="mt-8 h-px w-40 origin-left rounded-full bg-gold/70"
            initial={{ scaleX: reduced ? 1 : 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.4, delay: 1.1, ease }}
          />
        </div>

        <div className="flex flex-col gap-10 self-end pb-4 lg:col-span-4 lg:col-start-9 lg:pb-10" data-testid="hero-side">
          <motion.p
            className="max-w-sm font-sans text-base font-light leading-relaxed tracking-wide text-ivory/75"
            initial={{ opacity: 0, y: reduced ? 0 : 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.25, ease }}
            data-testid="hero-subcopy"
          >
            Where tension meets understanding. Professional massage
            therapy, tailored to your body. A space to leave the day at the
            door and exhale.
          </motion.p>
          <motion.div
            className="flex flex-col items-start gap-6"
            initial={{ opacity: 0, y: reduced ? 0 : 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.45, ease }}
          >
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-button"
              className="group flex items-center gap-3 rounded-full border border-gold px-9 py-4 font-sans text-xs font-medium uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold/15"
            >
              Book a Session
              <span className="h-[5px] w-[5px] rounded-full bg-gold transition-transform duration-500 group-hover:translate-x-1" />
            </a>
            <button
              onClick={() => scrollToId("#connection")}
              data-testid="hero-discover-link"
              className="group relative font-sans text-xs font-medium uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-500 hover:text-gold"
            >
              Discover our approach
              <span className="absolute -bottom-2 left-0 h-px w-full origin-left scale-x-100 bg-ivory/30 transition-transform duration-700 group-hover:scale-x-50 group-hover:bg-gold" />
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        aria-hidden="true"
      >
        <span className="font-sans text-[9px] uppercase tracking-[0.4em] text-ivory/40">Scroll</span>
        <span className="relative h-14 w-px overflow-hidden bg-ivory/15">
          <motion.span
            className="absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-gold"
            animate={reduced ? {} : { top: ["0%", "100%"] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
